import React, { useState, useEffect } from 'react';
import { X, Copy, Share2, QrCode, Check, ExternalLink } from 'lucide-react';
import { ShareableConfiguration, ShareService } from '../services/shareService';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  configuration: ShareableConfiguration;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, configuration }) => {
  const [shareUrl, setShareUrl] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsGenerating(true);
      try {
        const url = ShareService.generateShareUrl(configuration);
        setShareUrl(url);
        setQrCodeUrl(ShareService.generateQRCodeUrl(url));
      } catch (error) {
        console.error('Error generating share URL:', error);
      } finally {
        setIsGenerating(false);
      }
    }
  }, [isOpen, configuration]);

  const handleCopyUrl = async () => {
    const success = await ShareService.copyToClipboard(shareUrl);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleOpenShareUrl = () => {
    window.open(shareUrl, '_blank');
  };

  const handleShareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Webentwicklung Kalkulator - Meine Konfiguration',
          text: ShareService.formatConfigurationForDisplay(configuration),
          url: shareUrl,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to copy
      handleCopyUrl();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Share2 className="h-6 w-6 mr-2 text-blue-600" />
              Konfiguration teilen
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Configuration Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">Ihre Konfiguration:</h3>
            <div className="space-y-1 text-sm">
              {/* Show discount info if applied */}
              {configuration.discountEnabled && configuration.discountValue && configuration.discountValue > 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Zwischensumme:</span>
                    <span className="font-medium">€{configuration.subtotal}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">Rabatt ({configuration.discountValue}%):</span>
                    <span className="font-medium text-green-600">-€{configuration.discountAmount?.toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-700">Gesamtkosten:</span>
                <span className="font-medium text-blue-600">
                  €{configuration.discountEnabled && configuration.discountValue && configuration.discountValue > 0 
                    ? configuration.totalCost.toFixed(2) 
                    : configuration.totalCost}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Gesamtstunden:</span>
                <span className="font-medium">{configuration.totalHours}h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Erstellt am:</span>
                <span className="font-medium">{new Date(configuration.timestamp).toLocaleDateString('de-DE')}</span>
              </div>
            </div>
          </div>

          {/* Share URL */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teilbarer Link:
            </label>
            {isGenerating ? (
              <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Link wird generiert...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={handleCopyUrl}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                    copySuccess
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {copySuccess ? (
                    <>
                      <Check className="h-4 w-4 inline mr-1" />
                      Kopiert!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 inline mr-1" />
                      Kopieren
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* QR Code */}
          {qrCodeUrl && (
            <div className="mb-6 text-center">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                <QrCode className="h-4 w-4 inline mr-1" />
                QR-Code zum Teilen:
              </h3>
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
                <img
                  src={qrCodeUrl}
                  alt="QR Code"
                  className="w-32 h-32"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleOpenShareUrl}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Link öffnen
            </button>
            
            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <button
                onClick={handleShareNative}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Teilen
              </button>
            )}
          </div>

          {/* Info */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Hinweis:</strong> Dieser Link enthält Ihre komplette Konfiguration und kann 
              von anderen Personen verwendet werden, um Ihre Auswahl zu sehen und selbst eine 
              Anfrage zu stellen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
